import { useNavigate } from 'react-router-dom';
import { Star, MapPin, Heart } from 'lucide-react';
import { useFavorites } from '../../hooks/useFavorites';
import { WeddingVendor } from '../../types';

interface VendorCardProps {
    vendor: WeddingVendor;
    showCategory?: boolean;
    compact?: boolean;
}

export default function VendorCard({ vendor, showCategory = true, compact = false }: VendorCardProps) {
    const navigate = useNavigate();
    const { isFavorite, toggleFavorite } = useFavorites();

    // Smart image selection: ProfileImage -> PackageImage -> Placeholder
    const displayImage = vendor.images?.[0] ||
        vendor.packages?.find(p => p.images?.length && p.images.length > 0)?.images?.[0] ||
        'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400';

    // Calculate "Starts at" price
    const minPrice = vendor.packages && vendor.packages.length > 0
        ? Math.min(...vendor.packages.map(p => p.price))
        : null;

    return (
        <div
            onClick={() => navigate(`/weddings/vendor/${vendor.id}`)}
            className="group cursor-pointer bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 h-full flex flex-col"
        >
            {/* Image Container */}
            <div className={`overflow-hidden relative ${compact ? 'h-40' : 'h-52'}`}>
                <img
                    src={displayImage}
                    alt={vendor.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />

                {/* Rating Badge */}
                <div className="absolute top-3 right-3 bg-white/90 dark:bg-black/80 backdrop-blur px-2 py-1 rounded-lg flex items-center gap-1 shadow-sm">
                    <Star size={14} className="text-amber-500 fill-amber-500" />
                    <span className="text-xs font-bold text-gray-900 dark:text-white">
                        {vendor.rating.toFixed(1)}
                    </span>
                </div>

                {/* Favorite Button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(vendor.id);
                    }}
                    className="absolute top-3 left-3 p-2 bg-white/90 dark:bg-black/50 backdrop-blur rounded-full hover:bg-white transition-colors group/fav"
                >
                    <Heart
                        size={16}
                        className={`transition-colors ${isFavorite(vendor.id) ? 'text-rose-500 fill-rose-500' : 'text-white group-hover/fav:text-rose-500'}`}
                    />
                </button>
            </div>

            {/* Content */}
            <div className="p-4 flex-1 flex flex-col">
                {showCategory && (
                    <span className="text-xs font-medium text-rose-600 dark:text-rose-400 uppercase tracking-wider mb-1">
                        {vendor.category?.name || 'Vendor'}
                    </span>
                )}

                <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1 truncate">
                    {vendor.name}
                </h3>

                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mb-3">
                    <MapPin size={14} className="text-rose-500 flex-shrink-0" />
                    <span className="truncate">{vendor.city || 'Location available on request'}</span>
                </p>

                {/* Price Tag */}
                <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    {minPrice !== null ? (
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Starts at</span>
                            <span className="font-semibold text-gray-900 dark:text-white">
                                ₹{minPrice.toLocaleString()}
                            </span>
                        </div>
                    ) : (
                        <span className="text-xs text-gray-400 italic">Price on request</span>
                    )}
                </div>
            </div>
        </div>
    );
}
